// The Follow-up Settings form and the body PUT to v1/followupSettings.
//
// The two multi-selects hold what the backend stores, not the option objects:
// "Don't chase these project types" holds project type ids (excludedProjectTypeIds, D4)
// and "Chase quotes with these statuses" holds status names (eligibleQuoteStatuses).

export const WEEK_DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' }
]

export const ESCALATION_RECIPIENTS = [
  { value: 'SALESPERSON', label: 'The salesperson' },
  { value: 'CUSTOMER', label: 'The customer' },
  { value: 'BOTH', label: 'Both' }
]

function list(v) {
  return Array.isArray(v) ? v.slice() : []
}

function text(v) {
  return v === null || v === undefined ? '' : String(v)
}

/** The saved settings as the form's model. */
export function toForm(settings) {
  var s = settings || {}
  return {
    id: s.id,
    enabled: !!s.enabled,
    tenantDomains: text(s.tenantDomains),
    workStart: text(s.workStart),
    workEnd: text(s.workEnd),
    workDays: list(s.workDays).map(Number),
    dailyCap: s.dailyCap === undefined ? null : s.dailyCap,
    firstSendNeedsApproval: !!s.firstSendNeedsApproval,
    manualEnrolmentNeedsApproval: !!s.manualEnrolmentNeedsApproval,
    silenceEscalationDays: s.silenceEscalationDays === undefined ? null : s.silenceEscalationDays,
    escalationRecipient: s.escalationRecipient || null,
    escalationSubjectTemplate: text(s.escalationSubjectTemplate),
    escalationBodyTemplate: text(s.escalationBodyTemplate),
    customerEscalationSubjectTemplate: text(s.customerEscalationSubjectTemplate),
    customerEscalationBodyTemplate: text(s.customerEscalationBodyTemplate),
    internalRecipientOverride: text(s.internalRecipientOverride),
    excludedProjectTypeIds: list(s.excludedProjectTypeIds).map(Number),
    eligibleQuoteStatuses: list(s.eligibleQuoteStatuses).map(String),
    // on unless the backend says otherwise (its default is true)
    skipExpiredQuotes: s.skipExpiredQuotes !== false
  }
}

/**
 * The form as the PUT body. A multi-select may hold option objects if it was bound
 * without optionValue, so both shapes are reduced to the stored key. Only keys the backend
 * knows are sent (it answers 400 "Unknown settings" otherwise), so form-only fields stay out. A blank workStart
 * or workEnd is left out (the PUT is a partial update, so the saved time stays).
 */
export function toPayload(form) {
  var f = form || {}
  var ids = list(f.excludedProjectTypeIds).map(function (p) {
    return p && typeof p === 'object' ? Number(p.id) : Number(p)
  }).filter(function (n) { return !isNaN(n) })
  var statuses = list(f.eligibleQuoteStatuses).map(function (s) {
    return s && typeof s === 'object' ? s.statusName : s
  }).filter(function (s) { return typeof s === 'string' && s !== '' })
  var days = list(f.workDays).map(Number).filter(function (n) { return n >= 1 && n <= 7 })
  days.sort(function (a, b) { return a - b })
  var out = {
    enabled: !!f.enabled,
    tenantDomains: text(f.tenantDomains).trim(),
    workStart: text(f.workStart).trim(),
    workEnd: text(f.workEnd).trim(),
    workDays: days,
    dailyCap: f.dailyCap,
    firstSendNeedsApproval: !!f.firstSendNeedsApproval,
    manualEnrolmentNeedsApproval: !!f.manualEnrolmentNeedsApproval,
    silenceEscalationDays: f.silenceEscalationDays,
    escalationRecipient: f.escalationRecipient,
    escalationSubjectTemplate: text(f.escalationSubjectTemplate),
    escalationBodyTemplate: text(f.escalationBodyTemplate),
    customerEscalationSubjectTemplate: text(f.customerEscalationSubjectTemplate),
    customerEscalationBodyTemplate: text(f.customerEscalationBodyTemplate),
    internalRecipientOverride: text(f.internalRecipientOverride).trim(),
    excludedProjectTypeIds: ids,
    eligibleQuoteStatuses: statuses,
    skipExpiredQuotes: f.skipExpiredQuotes !== false
  }
  // The backend rejects "" as a time; a key left out keeps the saved value
  if (out.workStart === '') delete out.workStart
  if (out.workEnd === '') delete out.workEnd
  return out
}

/** One line for the toast after "Run now": the pass's counts, then the backend's note if it sent one. */
export function runNowSummary(result) {
  var r = result || {}
  var n = function (v) { return typeof v === 'number' ? v : 0 }
  var line = 'Sent ' + n(r.sent) + ', not sent ' + n(r.skipped) + ', failed ' + n(r.failed) +
    ', stopped ' + n(r.stopped) + ', escalated ' + n(r.escalated) + '.'
  return r.note ? line + ' ' + r.note : line
}
